import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { useGetItemQuery, useGetAllCategoriesQuery, useGetSubCategoriesByCategoryQuery, useGetCategoryPropertiesQuery, useCreateItemMutation, useUpdateItemMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ImageDropzone from '../../../components/ImageDropzone';
import { showSuccess, showError } from '../../../utils/toast';
import { useLanguage } from '../../../context/LanguageContext';

const AdminItemForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  
  const { data, isLoading } = useGetItemQuery(id!, { skip: !isEdit });
  const { data: categoriesData } = useGetAllCategoriesQuery(undefined);
  const [createItem, { isLoading: isCreating }] = useCreateItemMutation();
  const [updateItem, { isLoading: isUpdating }] = useUpdateItemMutation();

  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    category_id: '',
    sub_category_ids: [] as number[],
    description: '',
    description_ar: '',
    images: [] as File[],
  });
  
  const [existingImages, setExistingImages] = useState<string[]>([]);
  // Values entered for the category's dynamic properties, keyed by property id
  const [propertyValues, setPropertyValues] = useState<Record<number, { value: string; value_ar: string }>>({});

  // Fetch subcategories when category is selected
  const { data: subCategoriesData } = useGetSubCategoriesByCategoryQuery(
    formData.category_id || 0,
    { skip: !formData.category_id }
  );

  // Fetch the dynamic properties defined for the selected category
  const { data: categoryPropertiesData } = useGetCategoryPropertiesQuery(
    formData.category_id || 0,
    { skip: !formData.category_id }
  );

  useEffect(() => {
    if (isEdit && data?.data) {
      const item = data.data;
      setFormData({
        name: item.name || '',
        name_ar: item.name_ar || '',
        category_id: item.category_id || '',
        sub_category_ids: item.sub_categories?.map((sc: any) => sc.id || sc.sub_category_id) || [],
        description: item.description || '',
        description_ar: item.description_ar || '',
        images: [],
      });
      // Extract image filenames from full URLs
      if (item.images && Array.isArray(item.images)) {
        const imageFilenames = item.images.map((img: string) => {
          return img;
        });
        setExistingImages(imageFilenames);
      }
      // Pre-fill saved property values keyed by property id
      if (Array.isArray(item.properties)) {
        const savedValues: Record<number, { value: string; value_ar: string }> = {};
        item.properties.forEach((prop: any) => {
          savedValues[prop.property_id] = {
            value: prop.value || '',
            value_ar: prop.value_ar || '',
          };
        });
        setPropertyValues(savedValues);
      }
    }
  }, [isEdit, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      // Reset subcategories and property values when category changes
      if (name === 'category_id') {
        newData.sub_category_ids = [];
        setPropertyValues({});
      }
      return newData;
    });
  };

  const handlePropertyChange = (propertyId: number, field: 'value' | 'value_ar', val: string) => {
    setPropertyValues(prev => {
      const existing = prev[propertyId] || { value: '', value_ar: '' };
      return { ...prev, [propertyId]: { ...existing, [field]: val } };
    });
  };

  const handleSubCategoryChange = (selectedOptions: any) => {
    const subCategoryIds = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
    setFormData(prev => ({ ...prev, sub_category_ids: subCategoryIds }));
  };

  const handleImagesChange = (files: File[]) => {
    setFormData(prev => ({ ...prev, images: files }));
  };

  const handleRemoveExistingImage = (index: number) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('name_ar', formData.name_ar || '');
      submitData.append('category_id', formData.category_id);
      formData.sub_category_ids.forEach((id) => {
        submitData.append('sub_category_ids[]', id.toString());
      });
      submitData.append('description', formData.description || '');
      submitData.append('description_ar', formData.description_ar || '');
      
      // Append the category's dynamic property values (parallel arrays)
      categoryProperties.forEach((prop: any) => {
        submitData.append('property_id[]', prop.id.toString());
        submitData.append('property_val[]', propertyValues[prop.id]?.value || '');
        submitData.append('property_val_ar[]', propertyValues[prop.id]?.value_ar || '');
      });

      // Keep the existing images the user did not remove
      existingImages.forEach((img) => {
        submitData.append('existing_images[]', img);
      });

      // Append newly uploaded images
      formData.images.forEach((file) => {
        submitData.append('images[]', file);
      });

      if (isEdit) {
        const result = await updateItem({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || t('Item updated successfully!'));
      } else {
        const result = await createItem(submitData).unwrap();
        showSuccess(result.message || t('Item created successfully!'));
      }
      
      navigate('/admin/items');
    } catch (err: any) {
      showError(err?.data?.message || t(isEdit ? 'Failed to update item. Please try again.' : 'Failed to create item. Please try again.'));
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  const categories = categoriesData?.data || [];
  const subCategories = subCategoriesData?.data?.sub_categories || subCategoriesData?.data || [];
  const categoryProperties = categoryPropertiesData?.data || [];
  
  // Format subcategories for react-select
  const subCategoryOptions = subCategories.map((subCat: any) => ({
    value: subCat.id,
    label: `${subCat.title || subCat.name} - ${subCat.title_ar || subCat.name_ar || ''}`,
  }));
  
  // Get selected subcategories for react-select
  const selectedSubCategories = subCategoryOptions.filter((option: any) => 
    formData.sub_category_ids.includes(option.value)
  );

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? t('Edit Item') : t('Add New Item')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Dashboard')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/items'); }} className="text-muted text-hover-primary">{t('Items')}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 my-3">
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2"> {t('Name')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Item Name')}
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2"> {t('Name Arabic')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Item Name in Arabic')}
                        name="name_ar"
                        value={formData.name_ar}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Category:')}</label>
                      <select
                        className="form-select form-control-solid"
                        data-control="select2"
                        data-hide-search="true"
                        data-placeholder={t('Select Category')}
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">{t('Select Category')}</option>
                        {categories.map((category: any) => (
                          <option key={category.id} value={category.id}>{category.name} ({category.name_ar})</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('SubCategory:')}</label>
                      <Select
                        isMulti
                        isSearchable
                        options={subCategoryOptions}
                        value={selectedSubCategories}
                        onChange={handleSubCategoryChange}
                        placeholder={t('Select SubCategories')}
                        isDisabled={!formData.category_id}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '48px',
                            borderColor: '#e4e6ef',
                            '&:hover': {
                              borderColor: '#e4e6ef',
                            },
                          }),
                          multiValue: (base) => ({
                            ...base,
                            backgroundColor: '#f1faff',
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            color: '#009ef7',
                          }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: '#009ef7',
                            '&:hover': {
                              backgroundColor: '#009ef7',
                              color: 'white',
                            },
                          }),
                        }}
                      />
                    </div>

                    {formData.category_id && categoryProperties.length > 0 && (
                      <div className="col-md-12">
                        <h4 className="fw-bold mb-4">{t('Item Properties')}</h4>
                        <div className="row g-9">
                          {categoryProperties.map((property: any) => (
                            <React.Fragment key={property.id}>
                              <div className="col-md-6 fv-row fv-plugins-icon-container">
                                <label className="fs-6 fw-semibold mb-2">{property.name}</label>
                                <input
                                  type="text"
                                  className="form-control form-control-solid"
                                  placeholder={property.name}
                                  value={propertyValues[property.id]?.value || ''}
                                  onChange={(e) => handlePropertyChange(property.id, 'value', e.target.value)}
                                />
                              </div>
                              <div className="col-md-6 fv-row fv-plugins-icon-container">
                                <label className="fs-6 fw-semibold mb-2 d-block text-end" dir="rtl">{property.name_ar}</label>
                                <input
                                  type="text"
                                  dir="rtl"
                                  className="form-control form-control-solid text-end"
                                  placeholder={property.name_ar}
                                  value={propertyValues[property.id]?.value_ar || ''}
                                  onChange={(e) => handlePropertyChange(property.id, 'value_ar', e.target.value)}
                                />
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="col-md-12 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Description')}</label>
                      <textarea
                        name="description"
                        className="form-control form-control-solid"
                        placeholder={t('Write Description')}
                        rows={3}
                        value={formData.description}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-12 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Description Arabic')}</label>
                      <textarea
                        name="description_ar"
                        className="form-control form-control-solid"
                        placeholder={t('Write Description in Arabic')}
                        rows={3}
                        value={formData.description_ar}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-12 fv-row fv-plugins-icon-container">
                      <ImageDropzone
                        maxImages={10}
                        existingImages={existingImages}
                        onImagesChange={handleImagesChange}
                        onRemoveExisting={handleRemoveExistingImage}
                        label={t('Upload Images')}
                      />
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <button type="button" id="kt_modal_new_target_cancel" className="btn btn-light me-3" onClick={() => navigate('/admin/items')}>
                      {t('Discard')}
                    </button>
                    <button type="submit" id="kt_modal_new_target_submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">{t('Submit')}</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          {t('Please wait...')}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminItemForm;

